/**
 * AskTheBrokerTab — RAG-powered chat for agents to ask questions about
 * brokerage compliance documents, policies, and procedures.
 *
 * Route: /agent-portal/ask-the-broker
 * Backend: POST /chat/stream          (SSE streaming, agent_type='broker-ai')
 *          POST /chat/conversations   (create conversation)
 *          GET  /chat/conversations   (list conversations)
 *          GET  /chat/conversations/:id/messages (message history)
 *
 * The general-chat controller routes broker-ai to askTheBrokerService which
 * does two-layer semantic search over user_documents + document_chunks,
 * then answers via Bedrock Claude with citations.
 *
 * Design: SplitPanel (conversation list left, chat right), navy+gold palette.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faPlus,
  faRobot,
  faUser,
  faFileAlt,
  faLightbulb,
  faShieldAlt,
  faSpinner,
  faTrash,
  faComments,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import { useAtom } from 'jotai';
import { user_info } from '../../jotai/UserJotaiInstance';
import toast from 'react-hot-toast';
import { useSocket } from '../../hooks/useSocket';
import SplitPanel from '../broker-intelligence/shared/SplitPanel';
import SearchBar from '../broker-intelligence/shared/SearchBar';
import ListItem from '../broker-intelligence/shared/ListItem';
import { colors, fonts } from '../broker-intelligence/shared/colors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentReference {
  id?: string;
  filename?: string;
  document_type?: string;
  relevance_score?: number;
  snippet?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'broker';
  content: string;
  timestamp: Date;
  senderName?: string;
  documentReferences?: DocumentReference[];
  confidence?: number;
  suggestedFollowUps?: string[];
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  lastMessage?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Lightweight markdown → HTML for AI responses (no external dependency) */
function formatMarkdown(text: string): string {
  if (!text) return '';
  let html = text
    // Escape HTML entities first
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Headers: ## Header → <h3>
    .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 6px;font-size:13px;color:#0F1E35;">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:14px 0 8px;font-size:14px;font-weight:700;color:#0F1E35;">$1</h3>')
    // Bold: **text** → <strong>
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* → <em>
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
    // Bullet points: • or - at start of line
    .replace(/^[•\-]\s+(.+)$/gm, '<li style="margin:3px 0;margin-left:16px;">$1</li>')
    // Code: `text` → <code>
    .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:1px 5px;border-radius:3px;font-size:12px;">$1</code>')
    // Line breaks: double newline → paragraph break
    .replace(/\n\n/g, '<br/><br/>')
    // Single newline after a list item should not add <br>
    .replace(/<\/li>\n(?!<li)/g, '</li>')
    // Remaining single newlines
    .replace(/\n/g, '<br/>');
  return html;
}

const AskTheBrokerTab: React.FC = () => {
  const [userInfo] = useAtom(user_info);

  // Conversation list
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [conversationSearch, setConversationSearch] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Socket.io for real-time broker replies
  const { joinConversation, leaveConversation } = useSocket({
    userId: userInfo?.id?.toString(),
    onNewMessage: useCallback((message: any) => {
      if (message.metadata?.sender_id === userInfo?.id) return;
      if (activeConversation?.id && message.conversation_id === activeConversation.id) {
        const newMsg: ChatMessage = {
          id: message.id,
          role: (message.role || 'assistant') as 'user' | 'assistant' | 'broker',
          content: message.content,
          timestamp: new Date(message.timestamp),
          senderName: message.metadata?.sender_name,
        };
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, newMsg];
        });
      }
    }, [activeConversation?.id, userInfo?.id]),
  });

  // Auto-scroll on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const res = await axiosInstance.get('/chat/conversations');
      if (res.data.success || res.data.conversations) {
        // Filter to only broker-ai conversations
        const convs = (res.data.conversations || [])
          .filter((c: any) => c.agent_type === 'broker-ai')
          .map((c: any) => ({
            id: c.id,
            title: c.title || 'Untitled conversation',
            created_at: c.created_at,
            updated_at: c.updated_at || c.created_at,
            lastMessage: c.messages?.[0]?.content || '',
          }));
        setConversations(convs);
      }
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const loadConversation = useCallback(async (conv: Conversation) => {
    // Leave previous conversation room
    if (activeConversation?.id) leaveConversation(activeConversation.id);

    setActiveConversation(conv);
    setLoadingMessages(true);
    try {
      const res = await axiosInstance.get(`/chat/conversations/${conv.id}/messages`);
      if (res.data.success || res.data.messages) {
        const msgs = (res.data.messages || []).map((m: any) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant' | 'broker',
          content: m.content,
          timestamp: new Date(m.timestamp || m.created_at),
          senderName: m.metadata?.sender_name,
          documentReferences: m.metadata?.documentReferences,
          confidence: m.metadata?.confidence,
          suggestedFollowUps: m.metadata?.suggestedFollowUps,
        }));
        setMessages(msgs);
      }
      // Join Socket.io room for real-time broker replies
      joinConversation(conv.id);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingMessages(false);
    }
  }, [activeConversation?.id, joinConversation, leaveConversation]);

  // ---------------------------------------------------------------------------
  // Send message
  // ---------------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    const text = inputMessage.trim();
    if (!text || sending) return;

    // If no active conversation, create one first
    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const createRes = await axiosInstance.post('/chat/conversations', {
          agent_type: 'broker-ai',
          title: text.substring(0, 80) + (text.length > 80 ? '...' : ''),
        });
        convId = createRes.data.conversationId || createRes.data.conversation?.id;
        if (convId) {
          const newConv: Conversation = {
            id: convId,
            title: text.substring(0, 80) + (text.length > 80 ? '...' : ''),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            lastMessage: text,
          };
          setActiveConversation(newConv);
          setConversations((prev) => [newConv, ...prev]);
          joinConversation(convId);
        }
      } catch (err) {
        console.error('Failed to create conversation:', err);
        toast.error('Failed to start conversation');
        return;
      }
    }

    const userMsg: ChatMessage = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setSending(true);

    // AI response placeholder for streaming
    const aiPlaceholderId = `ai-${Date.now()}`;
    const aiPlaceholder: ChatMessage = {
      id: aiPlaceholderId,
      role: 'assistant',
      content: 'Searching documents...',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, aiPlaceholder]);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${axiosInstance.defaults.baseURL}chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        credentials: 'include',
        body: JSON.stringify({
          conversation_id: convId,
          message: text,
          agent_type: 'broker-ai',
          system_prompt: 'You are the Broker Assistant for the KairosIQ Interoffice Communications Hub. You have access to broker-specific resources, policies, and procedures. When users ask questions, search through available documents to provide accurate, broker-specific answers. Always cite the source document when providing information.',
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.type === 'chunk' && data.partial_response) {
                    fullText = data.partial_response;
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === aiPlaceholderId ? { ...m, content: fullText } : m
                      )
                    );
                  } else if (data.type === 'stream_complete') {
                    break;
                  }
                } catch {
                  // skip unparseable lines
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
    } catch (err: any) {
      console.error('Stream failed, trying fallback:', err);
      // Fallback to non-streaming
      try {
        const fallbackRes = await axiosInstance.post('/chat/chat', {
          conversation_id: convId,
          message: text,
          agent_type: 'broker-ai',
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aiPlaceholderId
              ? { ...m, content: fallbackRes.data.response || 'No response received' }
              : m
          )
        );
      } catch (fallbackErr: any) {
        toast.error('Failed to send message');
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id && m.id !== aiPlaceholderId));
        setInputMessage(text);
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [inputMessage, sending, activeConversation]);

  const handleNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const handleClearConversation = useCallback((convId: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== convId));
    if (activeConversation?.id === convId) {
      setActiveConversation(null);
      setMessages([]);
    }
  }, [activeConversation]);

  const handleFollowUp = (question: string) => {
    setInputMessage(question);
    inputRef.current?.focus();
  };

  // ---------------------------------------------------------------------------
  // Filtered conversations
  // ---------------------------------------------------------------------------

  const filteredConversations = conversationSearch
    ? conversations.filter((c) =>
        c.title?.toLowerCase().includes(conversationSearch.toLowerCase())
      )
    : conversations;

  // ---------------------------------------------------------------------------
  // Time formatting
  // ---------------------------------------------------------------------------

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // Confidence color
  const confidenceColor = (c?: number) => {
    if (!c) return colors.muted;
    if (c >= 0.8) return colors.green;
    if (c >= 0.5) return colors.orange;
    return colors.red;
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderWelcome = () => (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', padding: '40px',
      textAlign: 'center',
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24, boxShadow: '0 4px 20px rgba(201,149,42,0.3)',
      }}>
        <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 36, color: colors.navy }} />
      </div>
      <h2 style={{ color: colors.navy, fontFamily: fonts.display, fontSize: 28, margin: '0 0 8px' }}>
        Ask the Broker
      </h2>
      <p style={{ color: colors.muted, fontSize: 14, maxWidth: 440, lineHeight: 1.6, margin: '0 0 16px' }}>
        Ask questions about your brokerage's compliance documents, policies, and procedures.
        Get instant AI-powered answers with document citations.
      </p>
      <div style={{
        background: 'rgba(201,149,42,0.1)', border: `1px solid ${colors.gold}`,
        borderRadius: 8, padding: '10px 16px', maxWidth: 440, marginBottom: 24,
        fontSize: 12, color: colors.gold, lineHeight: 1.5, textAlign: 'left',
      }}>
        <strong>Disclosure:</strong> All conversations in Ask the Broker are monitored and
        reviewed by your brokerage for quality assurance, compliance, and agent support purposes.
        By using this tool, you acknowledge that your brokerage management has access to all
        messages and AI responses in this channel.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 400 }}>
        {[
          'What documents do I need for a new listing?',
          'What is our commission policy?',
          'What are the disclosure requirements in our state?',
        ].map((q) => (
          <button
            key={q}
            onClick={() => { setInputMessage(q); inputRef.current?.focus(); }}
            style={{
              background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy3}`,
              borderRadius: 8, padding: '12px 16px', fontSize: 13, fontFamily: fonts.body,
              cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.gold; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.navy3; }}
          >
            <FontAwesomeIcon icon={faLightbulb} style={{ color: colors.gold, marginRight: 10 }} />
            {q}
          </button>
        ))}
      </div>
    </div>
  );

  const renderMessage = (msg: ChatMessage) => {
    const isUser = msg.role === 'user';
    const isBroker = msg.role === 'broker';
    return (
      <div
        key={msg.id}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          marginBottom: 16,
        }}
      >
        {/* Avatar + name */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
          flexDirection: isUser ? 'row-reverse' : 'row',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: isBroker
              ? colors.navy2
              : isUser ? colors.navy3 : `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: isBroker ? `2px solid ${colors.gold}` : 'none',
          }}>
            <FontAwesomeIcon
              icon={isBroker ? faShieldAlt : isUser ? faUser : faRobot}
              style={{ fontSize: 12, color: isBroker ? colors.gold : isUser ? colors.muted : colors.navy }}
            />
          </div>
          <span style={{ fontSize: 11, color: isBroker ? colors.gold : colors.muted, fontWeight: 600 }}>
            {isBroker ? (msg.senderName || 'Your Broker') : isUser ? (userInfo?.user_first_name || 'You') : 'Broker Assistant'}
          </span>
          {isBroker && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: colors.navy,
              background: colors.gold, borderRadius: 4, padding: '1px 6px',
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              Broker
            </span>
          )}
          <span style={{ fontSize: 10, color: colors.muted }}>
            {formatTime(msg.timestamp)}
          </span>
        </div>

        {/* Message bubble */}
        <div style={{
          maxWidth: '75%',
          background: isBroker
            ? `linear-gradient(135deg, ${colors.navy2}, ${colors.navy3})`
            : isUser ? colors.navy2 : '#fff',
          color: isBroker ? colors.white : isUser ? colors.white : colors.navy,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          padding: '12px 16px',
          fontSize: 14,
          lineHeight: 1.6,
          fontFamily: fonts.body,
          whiteSpace: isUser ? 'pre-wrap' : 'normal',
          border: isBroker ? `1px solid ${colors.gold}` : isUser ? 'none' : `1px solid ${colors.border}`,
          boxShadow: isBroker ? '0 2px 8px rgba(201,149,42,0.15)' : isUser ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {isUser ? msg.content : (
            <div
              dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }}
              style={{ lineHeight: 1.7 }}
            />
          )}
        </div>

        {/* Document references */}
        {!isUser && msg.documentReferences && msg.documentReferences.length > 0 && (
          <div style={{ maxWidth: '75%', marginTop: 8 }}>
            <div style={{ fontSize: 11, color: colors.muted, fontWeight: 600, marginBottom: 4 }}>
              <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 4 }} />
              Sources ({msg.documentReferences.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {msg.documentReferences.map((ref, i) => (
                <div key={i} style={{
                  background: colors.navy2, color: colors.gold2, borderRadius: 6,
                  padding: '4px 10px', fontSize: 11, fontWeight: 500,
                  border: `1px solid ${colors.navy3}`,
                }}>
                  {ref.filename || ref.document_type || `Document ${i + 1}`}
                  {ref.relevance_score != null && (
                    <span style={{ marginLeft: 6, color: colors.muted, fontSize: 10 }}>
                      {Math.round(ref.relevance_score * 100)}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confidence */}
        {!isUser && msg.confidence != null && (
          <div style={{
            marginTop: 6, fontSize: 11,
            color: confidenceColor(msg.confidence),
            fontWeight: 600,
          }}>
            Confidence: {Math.round(msg.confidence * 100)}%
          </div>
        )}

        {/* Suggested follow-ups */}
        {!isUser && msg.suggestedFollowUps && msg.suggestedFollowUps.length > 0 && (
          <div style={{ maxWidth: '75%', marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {msg.suggestedFollowUps.map((q, i) => (
              <button
                key={i}
                onClick={() => handleFollowUp(q)}
                style={{
                  background: 'transparent', border: `1px solid ${colors.gold}`,
                  borderRadius: 16, padding: '4px 12px', fontSize: 11,
                  color: colors.gold, cursor: 'pointer', fontFamily: fonts.body,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = colors.gold;
                  e.currentTarget.style.color = colors.navy;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = colors.gold;
                }}
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------

  return (
    <SplitPanel
      leftWidth={320}
      left={
        <>
          {/* Header */}
          <div style={{
            padding: '16px', borderBottom: `1px solid ${colors.navy3}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ color: colors.white, fontWeight: 700, fontSize: 15, fontFamily: fonts.body }}>
              Conversations
            </span>
            <button
              onClick={handleNewConversation}
              style={{
                background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                border: 'none', borderRadius: 6, padding: '6px 12px',
                color: colors.navy, fontWeight: 700, fontSize: 12,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> New
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <SearchBar
              value={conversationSearch}
              onChange={setConversationSearch}
              placeholder="Search conversations..."
            />
          </div>

          {/* Conversation list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingConversations ? (
              <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faComments} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                <div style={{ fontSize: 13 }}>
                  {conversations.length === 0
                    ? 'No conversations yet. Start by asking a question!'
                    : 'No matching conversations'}
                </div>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div key={conv.id} style={{ position: 'relative' }}>
                  <ListItem
                    selected={activeConversation?.id === conv.id}
                    onClick={() => loadConversation(conv)}
                    title={conv.title || 'Untitled conversation'}
                    subtitle={conv.lastMessage}
                    meta={formatTime(new Date(conv.updated_at || conv.created_at))}
                  />
                  {activeConversation?.id === conv.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleClearConversation(conv.id); }}
                      style={{
                        position: 'absolute', top: 12, right: 12,
                        background: 'transparent', border: 'none',
                        color: colors.muted, cursor: 'pointer', fontSize: 12,
                      }}
                      title="Delete conversation"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      }
      right={
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Monitoring disclosure banner */}
          <div style={{
            padding: '6px 24px', background: colors.navy2,
            borderBottom: `1px solid ${colors.navy3}`,
            fontSize: 11, color: colors.gold, textAlign: 'center',
            fontFamily: fonts.body,
          }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 6, fontSize: 10 }} />
            This conversation is monitored by your brokerage for compliance and quality assurance purposes.
          </div>

          {/* Chat header */}
          <div style={{
            padding: '14px 24px', borderBottom: `1px solid ${colors.border}`,
            background: '#fff', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ color: colors.navy, fontSize: 16 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: colors.navy, fontFamily: fonts.body }}>
                Ask the Broker
              </div>
              <div style={{ fontSize: 11, color: colors.green, fontWeight: 500 }}>
                AI-powered — searches your brokerage documents
              </div>
            </div>
          </div>

          {/* Messages area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {messages.length === 0 && !loadingMessages ? (
              renderWelcome()
            ) : loadingMessages ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: colors.muted }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} /> Loading messages...
              </div>
            ) : (
              <>
                {messages.map(renderMessage)}
                {sending && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.muted, fontSize: 13 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FontAwesomeIcon icon={faRobot} style={{ fontSize: 12, color: colors.navy }} />
                    </div>
                    <FontAwesomeIcon icon={faSpinner} spin /> Searching documents...
                  </div>
                )}
                <div ref={chatEndRef} />
              </>
            )}
          </div>

          {/* Input area */}
          <div style={{
            padding: '16px 24px', borderTop: `1px solid ${colors.border}`,
            background: '#fff',
          }}>
            {/* Compliance disclosure */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', marginBottom: 6,
              background: '#FFF8E7', borderRadius: 8,
              borderLeft: `3px solid ${colors.gold}`,
            }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ color: colors.gold, fontSize: 12, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#6B5D3E', lineHeight: 1.4, fontFamily: fonts.body }}>
                This AI assistant is a reference tool only. It is not a substitute for professional guidance. If you are unsure about any aspect of a transaction, contact your Designated Broker directly.
              </span>
            </div>
            <div style={{
              display: 'flex', gap: 10, alignItems: 'flex-end',
              background: colors.offBg, borderRadius: 12,
              border: `1px solid ${colors.border}`, padding: '8px 12px',
            }}>
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask about policies, documents, compliance..."
                rows={1}
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  resize: 'none', outline: 'none', fontSize: 14,
                  fontFamily: fonts.body, color: colors.navy,
                  lineHeight: 1.5, maxHeight: 120,
                }}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={handleSend}
                disabled={sending || !inputMessage.trim()}
                style={{
                  background: inputMessage.trim()
                    ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`
                    : colors.border,
                  border: 'none', borderRadius: 8,
                  width: 38, height: 38, cursor: inputMessage.trim() ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                <FontAwesomeIcon
                  icon={sending ? faSpinner : faPaperPlane}
                  spin={sending}
                  style={{ color: inputMessage.trim() ? colors.navy : colors.muted, fontSize: 14 }}
                />
              </button>
            </div>
            <div style={{ fontSize: 11, color: colors.muted, marginTop: 6, textAlign: 'center' }}>
              This conversation is monitored by your brokerage. Press Enter to send.
            </div>
          </div>
        </div>
      }
    />
  );
};

export default AskTheBrokerTab;
